import { createFileRoute } from '@tanstack/react-router';
import { mw, MW_CARD_SHADOW, MW_CODE_STYLE } from '@/shared/mobile/tokens';
import { useMemo, useState, type CSSProperties } from 'react';
import { Accordion, Button, Center, Group, Loader, Modal, Stack, Text, TextInput, SimpleGrid, rem } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { notifyError } from '@/shared/lib/notify';
import {
  IconAlertTriangle, IconCheck, IconFolders, IconKey, IconLanguage, IconListSearch, IconPlus, IconSearch,
  IconWorld, IconX,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchList } from '@/shared/api/jsonapi/client';
import { httpRequest } from '@/shared/api/client';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { MobileHeader } from '@/shared/mobile/MobileHeader';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { StatCard } from '@/shared/components/StatCard';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

export const Route = createFileRoute('/_app/admin/translate-structure')({ component: TranslateStructurePage });

interface TranslationRow extends Denormalized {
  type: 'translations';
  id: string;
  languageCode?: string;
  translationWeb?: string;
}

interface NewKeyForm {
  section: string;
  group: string;
  label: string;
  defaultIt: string;
  defaultEn: string;
}

const INITIAL: NewKeyForm = { section: 'general', group: '', label: '', defaultIt: '', defaultEn: '' };

/**
 * Schema interno (in ogni Translation.translationWeb):
 *  { "general": { "auth": { "login": "Accedi" } }, "component": { … } }
 * type → primaryRow(group) → { label: value }.
 */
type TranslationTree = Record<string, Record<string, Record<string, unknown>>>;

function safeParse(json?: string): TranslationTree {
  if (!json) return {};
  try {
    const v = JSON.parse(json);
    return (typeof v === 'object' && v !== null ? v : {}) as TranslationTree;
  } catch {
    return {};
  }
}

const LANG_LABEL: Record<string, string> = {
  it: 'Italiano', en: 'English', fr: 'Français', de: 'Deutsch', es: 'Español',
};

const CARD: CSSProperties = {
  border: `1px solid ${mw.line}`, borderRadius: 16, background: mw.surface, boxShadow: MW_CARD_SHADOW,
};

function TranslateStructurePage() {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['translations', 'all-for-structure'],
    queryFn: () => fetchList<TranslationRow>('translations', { page: { size: 50 } }),
  });

  const languages = useMemo(
    () => (data?.data ?? []).map((r) => r.languageCode).filter(Boolean) as string[],
    [data],
  );
  const [lang, setLang] = useState('');
  const activeLang = lang || (languages.includes('it') ? 'it' : languages[0] ?? 'it');
  const activeRow = useMemo(() => data?.data.find((r) => r.languageCode === activeLang), [data, activeLang]);
  const tree = useMemo<TranslationTree>(() => safeParse(activeRow?.translationWeb), [activeRow]);

  const counts = useMemo(() => {
    let sections = 0; let groups = 0; let keys = 0;
    for (const gs of Object.values(tree)) {
      sections++;
      for (const ls of Object.values(gs ?? {})) { groups++; keys += Object.keys(ls ?? {}).length; }
    }
    return { sections, groups, keys };
  }, [tree]);

  const [filter, setFilter] = useState('');
  const sections = useMemo(() => {
    const all = Object.entries(tree).map(([type, groups]) => ({
      type,
      groups: Object.entries(groups ?? {}).map(([group, labels]) => ({
        group,
        labels: Object.entries(labels ?? {}).map(([k, v]) => ({ key: k, value: typeof v === 'string' ? v : JSON.stringify(v) })),
      })),
    }));
    if (!filter.trim()) return all;
    const q = filter.trim().toLowerCase();
    return all
      .map((s) => ({
        ...s,
        groups: s.groups
          .map((g) => ({
            ...g,
            labels: g.labels.filter(
              (l) => l.key.toLowerCase().includes(q)
                || l.value.toLowerCase().includes(q)
                || g.group.toLowerCase().includes(q)
                || s.type.toLowerCase().includes(q),
            ),
          }))
          .filter((g) => g.labels.length > 0 || g.group.toLowerCase().includes(q)),
      }))
      .filter((s) => s.groups.length > 0 || s.type.toLowerCase().includes(q));
  }, [tree, filter]);

  const [modalOpened, modal] = useDisclosure(false);
  const form = useForm<NewKeyForm>({
    mode: 'controlled',
    initialValues: INITIAL,
    validate: {
      section: (v) => (v.trim() ? null : 'Sezione obbligatoria'),
      group: (v) => (v.trim() ? null : 'Gruppo obbligatorio'),
      label: (v) => (v.trim() ? null : 'Label obbligatoria'),
    },
  });

  const addKey = useMutation({
    mutationFn: async (v: NewKeyForm) => {
      const rows = data?.data ?? [];
      // Best-effort: aggiorna anche il languageSetup di Setup (consumato da altri client).
      await httpRequest('/translations/addVoice', {
        method: 'POST', endpointType: 'plain',
        body: { type: v.section, primaryRow: v.group, label: '', value: '' },
      }).catch(() => {/* primary row già esistente: ok */});
      await httpRequest('/translations/addVoice', {
        method: 'POST', endpointType: 'plain',
        body: { type: v.section, primaryRow: v.group, label: v.label, value: v.defaultIt },
      }).catch(() => {/* best-effort: la fonte di verità UI è la PATCH sotto */});

      // Fonte di verità della UI = le row `translations`. Patchiamo OGNI lingua così la
      // chiave compare subito e le lingue restano strutturalmente allineate.
      await Promise.all(rows.map((r) => {
        const cur = safeParse(r.translationWeb);
        cur[v.section] ??= {};
        cur[v.section][v.group] ??= {};
        cur[v.section][v.group][v.label] = r.languageCode === 'en' ? (v.defaultEn || v.defaultIt) : v.defaultIt;
        return httpRequest(`/api/translations/${r.id}`, {
          method: 'PATCH',
          body: { data: { type: 'translations', id: r.id, attributes: { translationWeb: JSON.stringify(cur) } } },
        });
      }));
    },
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Chiave aggiunta a tutte le lingue' });
      qc.invalidateQueries({ queryKey: ['translations'] });
      modal.close();
      form.reset();
    },
    onError: (e) => notifyError(e),
  });

  const chip = (label: string, bg: string, color: string, codice = false) => (
    <span style={{ display: 'inline-block', fontSize: codice ? 'var(--fs-compact)' : 'var(--fs-compact)', fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: bg, color, ...(codice ? MW_CODE_STYLE : null), whiteSpace: 'nowrap' }}>{label}</span>
  );

  const isMobile = useIsMobileShell();
  const headActions = (
    <Button color="dark" variant="light" leftSection={<IconPlus size={16} />}
      onClick={() => { form.setValues(INITIAL); modal.open(); }}>
      Nuova chiave
    </Button>
  );

  const body = (
    <>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing={14} mb={18}>
        <StatCard label="Lingue" value={languages.length} icon={IconWorld} color="brand" loading={isLoading} />
        <StatCard label="Sezioni" value={counts.sections} icon={IconFolders} color="blue" loading={isLoading} />
        <StatCard label="Gruppi" value={counts.groups} icon={IconListSearch} color="grape" loading={isLoading} />
        <StatCard label="Chiavi" value={counts.keys} icon={IconKey} color="green" loading={isLoading} />
      </SimpleGrid>

      <div style={{ ...CARD, padding: '14px 16px', marginBottom: 16 }}>
        <Group wrap="wrap" gap="sm" align="center">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {languages.map((lc) => {
              const active = lc === activeLang;
              return (
                <button key={lc} type="button" className="mw-pressable" onClick={() => setLang(lc)}
                  style={{ height: rem(34), padding: '0 14px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-compact)', fontWeight: 600, whiteSpace: 'nowrap', border: active ? `1px solid ${mw.brand}` : `1px solid ${mw.line}`, background: active ? mw.brandFill : mw.surface, color: active ? 'var(--on-brand)' : mw.ink500 }}>
                  {LANG_LABEL[lc] ?? lc.toUpperCase()}
                </button>
              );
            })}
          </div>
          <TextInput
            placeholder="Filtra per section / group / label / valore…"
            leftSection={<IconSearch size={14} />}
            rightSection={filter ? (
              <button type="button" aria-label="Pulisci" onClick={() => setFilter('')} style={{ border: 0, background: 'transparent', cursor: 'pointer', color: mw.ink300, display: 'flex' }}><IconX size={13} /></button>
            ) : null}
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            style={{ marginLeft: 'auto', minWidth: 300, flex: 1 }}
          />
        </Group>
      </div>

      {isLoading ? (
        <Center py={60}><Loader /></Center>
      ) : isError ? (
        <div style={{ ...CARD, padding: '28px 20px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span style={{ width: 42, height: 42, borderRadius: 12, background: mw.dangerBg, color: mw.danger, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <IconAlertTriangle size={22} />
          </span>
          <div>
            <Text style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, color: mw.ink900 }}>Errore di caricamento traduzioni</Text>
            <Button mt={12} size="xs" variant="light" color="dark" onClick={() => qc.invalidateQueries({ queryKey: ['translations'] })}>Riprova</Button>
          </div>
        </div>
      ) : sections.length === 0 ? (
        <div style={{ ...CARD, padding: '36px 20px', textAlign: 'center' }}>
          <span style={{ display: 'inline-flex', width: 52, height: 52, borderRadius: 14, background: mw.accentTint, color: mw.accentText, alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
            <IconLanguage size={26} />
          </span>
          <Text style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, color: mw.ink900 }}>Nessuna chiave trovata</Text>
          <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, marginTop: 4 }}>Prova ad allargare la ricerca o cambiare lingua.</Text>
        </div>
      ) : (
        <Stack gap={14}>
          {sections.map((s) => {
            const keyCount = s.groups.reduce((n, g) => n + g.labels.length, 0);
            return (
              <div key={s.type} style={{ ...CARD, overflow: 'hidden' }}>
                <Group justify="space-between" p={16} pb={12} style={{ borderBottom: `1px solid ${mw.line}` }}>
                  <Group gap={10}>
                    <span style={{ width: 34, height: 34, borderRadius: 10, background: mw.brandTint, color: mw.brand, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconFolders size={18} /></span>
                    <div>
                      <Text style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, color: mw.ink900 }}>{s.type}</Text>
                      <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>{s.groups.length} gruppi · {keyCount} chiavi</Text>
                    </div>
                  </Group>
                </Group>
                <Accordion multiple variant="default" chevronPosition="left"
                  styles={{ control: { paddingInline: 16 }, content: { paddingInline: 16 }, item: { borderBottom: `1px solid ${mw.line}` } }}>
                  {s.groups.map((g) => (
                    <Accordion.Item key={`${s.type}.${g.group}`} value={`${s.type}.${g.group}`}>
                      <Accordion.Control>
                        <Group gap={8}>
                          <Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 600, color: mw.ink900 }}>{g.group}</Text>
                          {chip(String(g.labels.length), mw.surface2, mw.ink500)}
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap={0}>
                          {g.labels.length === 0 && <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink300, paddingBottom: 8 }}>Nessuna label.</Text>}
                          {g.labels.map((l, i) => (
                            <Group key={l.key} justify="space-between" wrap="nowrap" gap="md"
                              style={{ padding: '8px 10px', borderRadius: 10, background: i % 2 ? mw.surface2 : 'transparent' }}>
                              <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, ...MW_CODE_STYLE, whiteSpace: 'nowrap' }}>
                                <span style={{ color: mw.ink300 }}>{s.type}.{g.group}.</span><span style={{ color: mw.ink900, fontWeight: 600 }}>{l.key}</span>
                              </Text>
                              <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink900, textAlign: 'right' }} lineClamp={1}>{l.value || <span style={{ color: mw.ink300 }}>—</span>}</Text>
                            </Group>
                          ))}
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </div>
            );
          })}
        </Stack>
      )}
    </>
  );

  const modalEl = (
      <Modal
        opened={modalOpened}
        onClose={() => { modal.close(); form.reset(); }}
        title="Nuova chiave di traduzione"
        centered
        size="lg"
      >
        <form onSubmit={form.onSubmit((v) => addKey.mutate(v))}>
          <Stack>
            <Group grow>
              <TextInput label="Sezione (type)" description="Es. general, component, template" placeholder="general" required {...form.getInputProps('section')} />
              <TextInput label="Gruppo (primaryRow)" description="Es. auth, user, dashboard" placeholder="auth" required {...form.getInputProps('group')} />
            </Group>
            <TextInput label="Label" description="La chiave finale, es. login, profile" placeholder="login" required {...form.getInputProps('label')} />
            <Group grow>
              <TextInput label="Default IT" placeholder="Accedi" {...form.getInputProps('defaultIt')} />
              <TextInput label="Default EN" placeholder="Sign in" {...form.getInputProps('defaultEn')} />
            </Group>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: mw.accentTint, borderRadius: 12, padding: '10px 14px' }}>
              <IconCheck size={16} style={{ color: mw.accentText, flexShrink: 0, marginTop: 2 }} />
              <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>
                Verrà aggiunta la chiave <code>{form.values.section}.{form.values.group}.{form.values.label || '_'}</code> a tutte le {languages.length || '—'} lingue.
              </Text>
            </div>
            <Group justify="flex-end">
              <Button variant="subtle" color="dark" onClick={() => { modal.close(); form.reset(); }}>Annulla</Button>
              <Button type="submit" loading={addKey.isPending} style={{ background: mw.brandFill }}>Aggiungi</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
  );

  if (isMobile) {
    return (
      <Stack gap={16} px={16} pt={16} pb={24}>
        <MobileHeader title="Struttura traduzioni" />
        <Group justify="flex-end">{headActions}</Group>
        {body}
        {modalEl}
      </Stack>
    );
  }

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconLanguage size={22} />}
        title="Struttura traduzioni"
        sub="Chiavi i18n raggruppate per section · group · label, con default per lingua."
        tone="azzurro"
        actions={headActions}
      />
      {body}
      {modalEl}
    </MaeShell>
  );
}
